/** The HTTP status on an error from HttpClient, or anything shaped like one. */
export function httpStatus(err: unknown): number | undefined {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Why something failed, as a sentence someone can act on. Status codes,
 * addresses and stack traces stay out of it. `notFound` says what a 404 means
 * for this particular request, because only the caller knows that.
 */
export function failureReason(err: unknown, notFound: string): string {
  const status = httpStatus(err);
  if (status === 404) return notFound;
  if (status === 0) return 'Check your connection, then try again.';
  if (status === 401) return 'Your session has ended. Sign in again.';
  if (status === 403) return 'Your role doesn’t allow this. Ask an administrator if you need it.';
  if (status !== undefined && status >= 500) return 'The server had a problem. Try again in a minute.';
  return serverSentence(err) ?? 'Try again in a minute.';
}

/** A message the server wrote for people, if there is one; framework errors don't count. */
function serverSentence(err: unknown): string | undefined {
  const body = (err as { error?: unknown } | null)?.error;
  if (!body || typeof body !== 'object') return undefined;
  const fields = body as Record<string, unknown>;
  const text = [fields['message'], fields['detail']].find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0
  );
  if (!text || text.length > 160) return undefined;
  if (/https?:\/\/|Http failure|Exception|No static resource|\/api\//i.test(text)) return undefined;
  return text.trim();
}
